export type SoundEffect = "move" | "treasure" | "trap" | "win" | "loss" | "ui";

export interface AudioAdapter {
  setVolumes(music: number, effects: number): void;
  playEffect(effect: SoundEffect): void;
  startMusic(): void;
  stopMusic(): void;
  resume(): Promise<void>;
  dispose(): void;
}

export class NoopAudioAdapter implements AudioAdapter {
  setVolumes(_music: number, _effects: number): void {}
  playEffect(_effect: SoundEffect): void {}
  startMusic(): void {}
  stopMusic(): void {}
  async resume(): Promise<void> {}
  dispose(): void {}
}

export class BrowserAudioAdapter implements AudioAdapter {
  private context?: AudioContext;
  private effectsVolume = 0.8;
  private musicVolume = 0.7;
  private music?: { oscillator: OscillatorNode; gain: GainNode };

  setVolumes(music: number, effects: number): void {
    this.musicVolume = Math.max(0, Math.min(1, music));
    this.effectsVolume = Math.max(0, Math.min(1, effects));
    this.music?.gain.gain.setTargetAtTime(this.musicVolume * 0.035, this.context?.currentTime ?? 0, 0.05);
  }

  async resume(): Promise<void> {
    this.context ??= new AudioContext();
    if (this.context.state === "suspended") await this.context.resume();
  }

  playEffect(effect: SoundEffect): void {
    if (!this.context || this.effectsVolume === 0) return;
    const frequencies: Record<SoundEffect, number> = {
      move: 180, treasure: 620, trap: 95, win: 880, loss: 120, ui: 340,
    };
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = effect === "trap" || effect === "loss" ? "sawtooth" : "sine";
    oscillator.frequency.value = frequencies[effect];
    gain.gain.setValueAtTime(0.0001, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12 * this.effectsVolume, this.context.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + 0.12);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start();
    oscillator.stop(this.context.currentTime + 0.13);
  }

  startMusic(): void {
    if (!this.context || this.music || this.musicVolume === 0) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = 82.41;
    gain.gain.value = this.musicVolume * 0.035;
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start();
    this.music = { oscillator, gain };
  }

  stopMusic(): void {
    this.music?.oscillator.stop();
    this.music = undefined;
  }

  dispose(): void {
    this.stopMusic();
    void this.context?.close();
    this.context = undefined;
  }
}
